import React, { useState } from 'react';
import { ExternalLink, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { portfolioData } from '../utils/mockData';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', 'Full Service', 'Performance Marketing', 'Creative & Branding', 'Social Media & SEO', 'Influencer Marketing'];

  const filteredProjects = selectedCategory === 'All' 
    ? portfolioData.slice(0, 4)
    : portfolioData.filter(project => project.category === selectedCategory).slice(0, 4);

  return (
    <section id="portfolio" className="py-24 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-orange-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Our Work
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Success Stories That Inspire
          </h2>
          <p className="text-xl text-slate-600">
            Real results from real campaigns. See how we've helped brands achieve remarkable growth.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
              className={`${
                selectedCategory === category
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'bg-white border-slate-300 text-slate-700 hover:border-orange-600 hover:text-orange-600'
              } transition-all duration-300`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <Card
              key={project.id}
              className="group overflow-hidden border-slate-200 hover:shadow-2xl transition-all duration-300 cursor-pointer"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <Button
                    size="sm"
                    className="bg-white text-slate-900 hover:bg-orange-600 hover:text-white"
                  >
                    View Details
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>

              <CardContent className="p-6">
                <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 mb-3">
                  {project.category}
                </Badge>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  {project.title}
                </h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Metrics */}
                <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-slate-900">{value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
